import React, { useEffect, useState } from 'react';
import '@hassanmojab/react-modern-calendar-datepicker/lib/DatePicker.css';
import DatePicker, {
  DayValue,
  DatePickerProps,
} from '@hassanmojab/react-modern-calendar-datepicker';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import Container from 'saur_ui_library/src/Atoms/Container/Container';
import { colors } from 'saur_ui_library/src/Theme';
import Word from 'saur_ui_library/src/Atoms/Word/Word';
import { motion } from 'framer-motion';
import { createGlobalStyle } from 'styled-components';
import { formatDatePickerDate } from '../../../utils/helper';

type DatePickerProps2 = {
  className?: string;
  inputClassName?: string;
  errorMessage?: string | null;
  onChange: any;
  label?: string;
  required?: boolean;
  calendarPopperPosition?: calendarPopperPosition;
} & DatePickerProps<DayValue>;

type calendarPopperPosition = 'auto' | 'top' | 'bottom';

const frLocal = {
  months: [
    'Janvier',
    'Février',
    'Mars',
    'Avril',
    'Mai',
    'Juin',
    'Juillet',
    'Août',
    'Septembre',
    'Octobre',
    'Novembre',
    'Décembre',
  ],

  // week days by order
  weekDays: [
    {
      name: 'Lundi',
      short: 'L',
    },
    {
      name: 'Mardi',
      short: 'M',
    },
    {
      name: 'Mercredi',
      short: 'M',
    },
    {
      name: 'Jeudi',
      short: 'J',
    },
    {
      name: 'Vendredi',
      short: 'V',
    },
    {
      name: 'Samedi',
      short: 'S',
      isWeekend: true,
    },
    {
      name: 'Dimanche', // used for accessibility
      short: 'D', // displayed at the top of days' rows
      isWeekend: true, // is it a formal weekend or not?
    },
  ],

  // just play around with this number between 0 and 6
  weekStartingIndex: 6,

  // return a { year: number, month: number, day: number } object
  getToday(gregorainTodayObject: any) {
    return gregorainTodayObject;
  },

  // return a native JavaScript date here
  toNativeDate(date: any) {
    return new Date(date.year, date.month - 1, date.day);
  },

  // return a number for date's month length
  getMonthLength(date: any) {
    return new Date(date.year, date.month, 0).getDate();
  },

  // return a transformed digit to your locale
  transformDigit(digit: any) {
    return digit;
  },

  // texts in the date picker
  nextMonth: 'Le mois prochain',
  previousMonth: 'le mois précédent',
  openMonthSelector: 'Ouvrir le sélecteur de mois',
  openYearSelector: "Ouvrir le sélecteur d'année",
  closeMonthSelector: 'Fermer le sélecteur de mois',
  closeYearSelector: "Fermer le sélecteur d'année",
  defaultPlaceholder: 'Sélectionner...',

  // for input range value
  from: 'de',
  to: 'à',

  // used for input value when multi dates are selected
  digitSeparator: ',',

  // if your provide -2 for example, year will be 2 digited
  yearLetterSkip: 0,

  // is your language rtl or ltr?
  isRtl: false,
};

const ModernDatePicker: React.FC<DatePickerProps2> = (props) => {
  function padLeadingZeros(num: number, size: number): string {
    let s = num + '';
    while (s.length < size) s = '0' + s;
    return s;
  }
  const [formatedDay, setFormatedDay] = useState('');
  const handleChange = (date: any) => {
    if (date) {
      props.onChange(date);
      const { day, month, year } = date;
      if (props.locale === 'en') {
        setFormatedDay(
          `${padLeadingZeros(month, 2)}   /   ${padLeadingZeros(
            day,
            2,
          )}   /   ${year}`,
        );
      } else {
        setFormatedDay(
          `${padLeadingZeros(day, 2)}   /   ${padLeadingZeros(
            month,
            2,
          )}   /   ${year}`,
        );
      }
    }
  };

  const CustomDatePickerInput = ({ ref }) => {
    return (
      <>
        <div className='date-picker-input' style={{ position: 'relative' }}>
          <input
            className={`${props.inputClassName} my-custom-input`}
            readOnly
            ref={ref}
            placeholder={props.inputPlaceholder}
            value={formatDatePickerDate(props.value)}
            style={{
              textAlign: 'center',
              // width: '327px',
            }}
          />
          <span
            style={{
              position: 'absolute',
              right: '10px',
              top: '50%',
              transform: 'translateY(-50%)',
            }}
          >
            <CalendarMonthIcon
              data-testid='calendar-icon'
              sx={{ color: '#69b5db' }}
            />
          </span>
        </div>
      </>
    );
  };

  useEffect(() => {
    handleChange(props?.value);
  }, [props?.value]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Container className={`sds-m-modern-date-picker ${props.className}`}>
      <Styles />
      {!!props.label && (
        <div>
          <Word
            className='sds-m-modern-date-picker-label'
            value={props.label} // translation
            textStyle='tinyRegular'
            variant='span'
          />
          {props.required && (
            <Word
              className='sds-m-modern-date-picker-label-required'
              variant='span'
              textStyle='tinyRegular'
              value='*'
            />
          )}
        </div>
      )}
      <div>
        <DatePicker
          inputPlaceholder={
            props.locale === 'en'
              ? 'MM   /   JJ   /   AAAA'
              : 'JJ   /   MM   /   AAAA'
          }
          formatInputText={() => formatedDay}
          {...props}
          onChange={handleChange}
          calendarPopperPosition={props.calendarPopperPosition ?? 'auto'}
          locale={props.locale === 'en' ? 'en' : frLocal}
          inputClassName={
            props.errorMessage
              ? 'my-custom-input date-error'
              : 'my-custom-input'
          } // custom class
          colorPrimary={colors.deepBlue}
          calendarClassName='custom-calendar'
          renderInput={CustomDatePickerInput}
        />
        {props.errorMessage && (
          <motion.div
            className='date-error-msg-container'
            animate={{ opacity: 1 }}
          >
            <Word
              className='date-error-msg'
              textStyle='normalMed'
              variant='p'
              value={props.errorMessage}
              errorId='date-error-message'
            />
          </motion.div>
        )}
      </div>
    </Container>
  );
};

export const Styles = createGlobalStyle`
.sds-reset-style{
  .sds-m-modern-date-picker{
    margin-bottom: 2px !important;
    .date-picker-input{
      span svg{
       width:30px;
       height:30px;
      }}}
}`;

export default ModernDatePicker;
